import { Component, Inject, Input, OnInit, signal, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import { Papa, ParseResult } from 'ngx-papaparse';
import { NotificationMedium } from '@vendasta/notifications-sdk';
import { BehaviorSubject, EMPTY, Observable } from 'rxjs';
import { catchError, distinctUntilChanged, finalize, map, tap } from 'rxjs/operators';
import { AdminService } from '../../common/notifications/notifications-admin.service';
import { FileInfo, FileUploadError, GALAXY_UPLOADER_SERVICE_TOKEN } from '@vendasta/galaxy/uploader';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { ReleaseFormStubUploadService } from './release-form-stub-upload.service';

interface FormState {
  typeId: string;
  medium: NotificationMedium;
}

interface State {
  isLoading: boolean;
  hasError: boolean;
  isSubmitting: boolean;
  name: string;
  availableMediums: NotificationMedium[];
  userIds: string[];
}

const INITIAL_STATE = {
  isLoading: true,
  hasError: false,
  isSubmitting: false,
  availableMediums: [],
  name: '',
  userIds: [],
};

@Component({
  selector: 'notification-release-form',
  templateUrl: './release-form.component.html',
  styleUrls: ['./release-form.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [{ provide: GALAXY_UPLOADER_SERVICE_TOKEN, useClass: ReleaseFormStubUploadService }],
})
export class ReleaseFormComponent implements OnInit {
  @Input() listRoute: string;
  @Input() typeId: string;

  protected readonly files = signal<FileInfo[]>([]);

  private readonly store = new BehaviorSubject<State>({ ...INITIAL_STATE });
  private get state(): State {
    return this.store.getValue();
  }
  private set state(newState: State) {
    this.store.next(newState);
  }

  public readonly isLoading$: Observable<boolean> = this.store.pipe(
    map((state) => state.isLoading),
    distinctUntilChanged(),
  );
  public readonly showError$: Observable<boolean> = this.store.pipe(
    map((state) => !state.isLoading && state.hasError),
    distinctUntilChanged(),
  );
  public readonly showContent$: Observable<boolean> = this.store.pipe(
    map((state) => !state.isLoading && !state.hasError),
    distinctUntilChanged(),
  );
  public readonly isSubmitting$: Observable<boolean> = this.store.pipe(
    map((state) => state.isSubmitting),
    distinctUntilChanged(),
  );
  public readonly name$: Observable<string> = this.store.pipe(
    map((state) => state.name),
    distinctUntilChanged(),
  );
  public readonly availableMediums$: Observable<NotificationMedium[]> = this.store.pipe(
    map((state) => state.availableMediums),
    distinctUntilChanged(),
  );
  public readonly userIds$: Observable<string[]> = this.store.pipe(
    map((state) => state.userIds),
    distinctUntilChanged(),
  );
  public readonly userIdsSet$: Observable<boolean> = this.store.pipe(
    map((state) => state.userIds.length > 0),
    distinctUntilChanged(),
  );

  public readonly form = this.fb.group({
    typeId: this.fb.control('', [Validators.required, Validators.pattern('[a-z-]+')]),
    medium: this.fb.control('', Validators.required),
  });

  constructor(
    @Inject('AdminService') private notificationsAdminService: AdminService,
    private fb: UntypedFormBuilder,
    private readonly snackBar: SnackbarService,
    private papa: Papa,
  ) {}

  ngOnInit(): void {
    if (!this.typeId) {
      this.state = { ...this.state, isLoading: false, hasError: true };
    } else {
      this.form.get('typeId').disable();
      this.state = { ...this.state };
      this.loadType();
    }
  }

  loadType(): void {
    this.state = { ...this.state, isLoading: true, hasError: false };
    this.notificationsAdminService
      .getType$(this.typeId)
      .pipe(
        tap((t) => {
          this.state = { ...this.state, name: t.name, isLoading: false };
          this.form.patchValue({
            typeId: t.notificationTypeId,
          });
          if (t.configurations) {
            this.state = { ...this.state, availableMediums: t.listEnabledMediums() };
          } else {
            throw new Error('type has no enabled mediums');
          }
        }),
        catchError(() => {
          this.state = { ...this.state, isLoading: false, hasError: true };
          return EMPTY;
        }),
      )
      .subscribe();
  }

  mediumToString(medium: NotificationMedium): string {
    if (medium === NotificationMedium.NOTIFICATION_MEDIUM_WEB) {
      return 'In-App';
    } else if (medium === NotificationMedium.NOTIFICATION_MEDIUM_EMAIL) {
      return 'Email';
    }
    return 'Unknown';
  }

  onSubmit(): void {
    if (!this.form.valid || this.files().length === 0) {
      this.snackBar.openErrorSnack(
        'The form has errors, please correct them before attempting to release the notification.',
      );
      return;
    }
    this.update();
    this.state = { ...this.state, isSubmitting: true };
  }

  private update(): void {
    const vals: FormState = this.form.getRawValue();
    this.notificationsAdminService
      .releaseType$(vals.typeId, vals.medium, this.state.userIds)
      .pipe(
        tap(() => this.snackBar.openSuccessSnack('Notification released.')),
        catchError(() => {
          this.snackBar.openErrorSnack('Failed to release notification.');
          return EMPTY;
        }),
        finalize(() => (this.state = { ...this.state, isSubmitting: false })),
      )
      .subscribe();
  }

  onError(event: FileUploadError): void {
    this.snackBar.openErrorSnack(event.error.message);
  }

  onFilesChanged(files: FileInfo[]): void {
    this.state = { ...this.state, userIds: [] };

    if (files.length > 0) {
      this.parseCSV(files[0].file as File);
    }
    this.files.set(files);
  }

  private parseCSV(file: File) {
    this.papa.parse(file, {
      header: false,
      delimiter: ',',
      complete: (results: ParseResult) => {
        if (!results.data[0][0].startsWith('U')) {
          this.snackBar.openErrorSnack('The file you uploaded has a header, please remove it.');
          return;
        }
        const userIds = results.data.map((r: string[]) => {
          return r[0];
        });
        this.state = { ...this.state, userIds: userIds };
      },
      error: (error) => this.snackBar.openErrorSnack('There was an error parsing the CSV: ' + error),
    });
  }
}
